import type { BlogCover } from "src/lib/BlogPosts";
import type { Metadata, MetadataImage } from "src/lib/Metadata";

/** A heading in a docs page's table of contents (DocsToc). */
export interface DocsHeading {
  id: string;
  value: string;
  depth: number;
}

/** `/docs/guides/webhooks` → `guides/webhooks`; `/docs` → ``. */
export const getDocsPath = (pathname: string) =>
  pathname.replace(/^\/docs(?:\/|$)/, "").replace(/\/$/, "");

/**
 * Where the social card of docs pages illustrated with `cover` is served (vite/docsSocialImages.ts):
 * the cover art drawn as the blog's cards are. Keyed by cover, so pages sharing art share the file and
 * a page whose art changes gets a new URL.
 */
export const docsSocialImagePath = (cover: BlogCover) => `/social/docs/${cover}.png`;

/** The URL pages link a docs social card at, versioned like the blog's (src/lib/BlogPosts.ts). */
export const getDocsSocialImageUrl = (cover: BlogCover) =>
  import.meta.env.PUBLIC_BLOG_SOCIAL_IMAGE_VERSION
    ? `${docsSocialImagePath(cover)}?${import.meta.env.PUBLIC_BLOG_SOCIAL_IMAGE_VERSION}`
    : docsSocialImagePath(cover);

/** The title a docs page shows in search results and social cards: `seoTitle`, else `title`. */
export const getDocsSearchTitle = (page: { title: string; seoTitle?: string }) =>
  page.seoTitle ?? page.title;

/**
 * A docs page's head: its search title and description, also as the canonical, Open Graph and X
 * card. Pages with artwork (`<DocsArtwork cover>`) get its social card; the others keep the site's
 * (ROOT_METADATA).
 */
export const getDocsPageMetadata = (page: {
  path: string;
  title: string;
  seoTitle?: string;
  description?: string;
  cover?: BlogCover;
}): Metadata => {
  const title = getDocsSearchTitle(page);
  const images: MetadataImage[] | undefined = page.cover
    ? [
        {
          url: getDocsSocialImageUrl(page.cover),
          type: "image/png",
          width: 1200,
          height: 630,
          alt: page.title,
        },
      ]
    : undefined;
  return {
    title,
    description: page.description,
    alternates: { canonical: page.path, markdown: `${page.path}.md` },
    openGraph: {
      type: "website",
      url: page.path,
      title,
      description: page.description,
      images,
    },
    twitter: {
      card: "summary_large_image",
      title,
      description: page.description,
      images,
    },
  };
};
